import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Send, ArrowLeft, MessageSquare, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    fetchConversations();
  }, [user]);

  useEffect(() => {
    if (activeConv) fetchMessages(activeConv.id);
  }, [activeConv]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const { data } = await axios.get(`${API}/conversations`);
      setConversations(data);
      if (data.length > 0 && !activeConv) setActiveConv(data[0]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchMessages = async (convId) => {
    try {
      const { data } = await axios.get(`${API}/conversations/${convId}/messages`);
      setMessages(data);
    } catch (e) { console.error(e); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/conversations/${activeConv.id}/messages`, { content: newMsg.trim() });
      setMessages((prev) => [...prev, data]);
      setNewMsg("");
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const getOtherName = (conv) => {
    if (!conv?.participant_names || !user) return "Partner";
    const other = Object.entries(conv.participant_names).find(([id]) => id !== user.id);
    return other?.[1] || "Partner";
  };

  const getOtherAvatar = (conv) => {
    if (!conv?.participant_avatars || !user) return "";
    const other = Object.entries(conv.participant_avatars).find(([id]) => id !== user.id);
    return other?.[1] || "";
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
      <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-16 flex flex-col" style={{ height: "100vh" }}>
      <div className="flex-1 flex overflow-hidden max-w-6xl mx-auto w-full">
        {/* Sidebar */}
        <div className="w-80 flex-shrink-0 border-r border-[#2A3143] bg-surface flex flex-col dark-section">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-white flex items-center gap-2">
              <MessageSquare size={16} className="text-brand-green" /> Messages
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                No conversations yet.{" "}
                <Link to="/find-partners" className="text-brand-green hover:underline">Find a partner</Link> to start messaging.
              </div>
            ) : (
              conversations.map((conv) => {
                const name = getOtherName(conv);
                const avatar = getOtherAvatar(conv);
                const isActive = activeConv?.id === conv.id;
                return (
                  <button
                    key={conv.id}
                    onClick={() => setActiveConv(conv)}
                    className={`w-full text-left p-4 hover:bg-gray-100 transition-colors border-b border-gray-200 ${isActive ? "bg-gray-100 border-l-2 border-l-lime" : ""}`}
                    data-testid={`conversation-item-${conv.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {avatar ? (
                        <img src={avatar} alt={name} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-brand-green/10 flex items-center justify-center text-brand-green font-bold flex-shrink-0">
                          {name[0]}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-white text-sm">{name}</div>
                        {conv.last_message && (
                          <div className="text-xs text-gray-500 truncate">{conv.last_message.content}</div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat area */}
        {activeConv ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Chat header */}
            <div className="p-4 border-b border-gray-200 bg-surface flex items-center gap-3">
              {getOtherAvatar(activeConv) ? (
                <img src={getOtherAvatar(activeConv)} alt="" className="w-9 h-9 rounded-lg object-cover" />
              ) : (
                <div className="w-9 h-9 rounded-lg bg-brand-green/10 flex items-center justify-center text-brand-green font-bold">
                  {getOtherName(activeConv)[0]}
                </div>
              )}
              <span className="font-bold text-brand-navy">{getOtherName(activeConv)}</span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-navy dark-section" data-testid="messages-list">
              {messages.map((msg) => {
                const isMe = msg.sender_id === user.id;
                return (
                  <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-2.5 rounded-xl text-sm ${
                      isMe ? "bg-brand-blue text-white rounded-br-sm" : "bg-white border border-gray-200 text-white rounded-bl-sm"
                    }`}>
                      {msg.content}
                      <div className={`text-xs mt-1 ${isMe ? "text-navy/60" : "text-gray-400"}`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-gray-200 bg-surface flex gap-3">
              <input
                type="text"
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 bg-navy border border-gray-200 rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green/50 dark-section"
                data-testid="message-input"
              />
              <button
                type="submit"
                disabled={!newMsg.trim() || sending}
                className="p-2.5 bg-brand-orange text-white rounded-lg hover:bg-brand-orange-hover transition-colors disabled:opacity-50"
                data-testid="send-message-button"
              >
                <Send size={18} />
              </button>
            </form>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageSquare size={48} className="mx-auto mb-3 text-slate-700" />
              <p className="text-sm">Select a conversation</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
